import { AlertMessage } from '@/app/components'
import LoadingAnimation from '@/components/LoadingAnimation/LoadingAnimation'
import ConfigureDeviceStepBody from '../components/ConfigureDeviceStepBody'
import ConfigureDeviceCard from '../components/ConfigureDeviceCard'
import ConfigureSettingRow from '../components/ConfigureSettingRow'
import ProgramPrescribedAlert from '../components/ProgramPrescribedAlert'
import useConfigureVehicleViewModel from './ConfigureVehicle.viewmodel'

export function ConfigureVehicle() {
  const {
    isLoadingOrIdle,
    vehicle,
    displayName,
    limitsText,
    goalsValueText,
    scheduleDescriptions,
    tripsValueText,
    goalsArePrescribed,
    prescribedGoals,
    settingPaths,
  } = useConfigureVehicleViewModel()

  if (isLoadingOrIdle) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingAnimation type="falling" />
      </div>
    )
  }

  const subtitle = vehicle?.car?.car_model
    ? `${vehicle.car.car_model.year} ${vehicle.car.car_model.friendly_name}`
    : undefined
  // Match the device-selection-gallery convention: car.image_url is a
  // relative path under /dashboard/img/car_models, with a covered-car
  // fallback when the field is missing.
  const carImageSrc = vehicle?.car?.image_url
    ? `/dashboard/img/car_models${vehicle.car.image_url}`
    : '/dashboard/img/car_models/covered-car.webp'
  const illustration = (
    <img
      src={carImageSrc}
      alt={displayName}
      className="h-12 w-auto object-contain"
    />
  )

  return (
    <ConfigureDeviceStepBody>
      <ConfigureDeviceCard
        title={displayName}
        subtitle={subtitle}
        illustration={illustration}
      >
        <ConfigureSettingRow
          iconName="BatteryCharging"
          title="Limits"
          description="Use this to adjust your car's charge limit"
          destinationPath={settingPaths.limits}
        >
          {limitsText}
        </ConfigureSettingRow>

        {goalsArePrescribed && (
          <>
            <ConfigureDeviceCard.Divider />
            {prescribedGoals.map((goal) => (
              <ProgramPrescribedAlert
                key={goal.label}
                settingDisplayName={goal.label}
                requiredDirection={goal.requiredDirection}
              />
            ))}
            <ConfigureSettingRow
              iconName="Target"
              title="Goals"
              description="Set charging goals with Optiwatt. Tell us what matters most to you"
              destinationPath={settingPaths.goals}
            >
              {goalsValueText}
            </ConfigureSettingRow>
          </>
        )}

        <ConfigureDeviceCard.Divider />

        <AlertMessage variant="info">
          Set your daily Ready By time so we can optimize your charging based on
          when you plan to leave.
        </AlertMessage>

        <ConfigureSettingRow
          iconName="Clock"
          title="Scheduling"
          description="Customize your ready by and start charge times for each day of the week"
          destinationPath={settingPaths.scheduling}
        >
          {scheduleDescriptions.length === 0 ? (
            'No scheduled departure/charging planned'
          ) : (
            <div className="flex flex-col">
              {scheduleDescriptions.map((description) => (
                <span key={description}>{description}</span>
              ))}
            </div>
          )}
        </ConfigureSettingRow>

        <ConfigureDeviceCard.Divider />

        <ConfigureSettingRow
          iconName="Calendar"
          title="Trips"
          description="Add charging preferences for any upcoming trips"
          destinationPath={settingPaths.trips}
        >
          {tripsValueText}
        </ConfigureSettingRow>
      </ConfigureDeviceCard>
    </ConfigureDeviceStepBody>
  )
}
